/**
 * Бланк форми фінансової звітності — візуально наближений до ОФІЦІЙНОЇ друкованої форми
 * (ч.1389, зауваження власника: «бухгалтери бояться скороченого вигляду — повні форми, як у
 * декларації з ПДВ»): шапка з блоком «КОДИ» (дата, ЄДРПОУ, КАТОТТГ, КОПФГ, КВЕД), підприємство,
 * територія, оргформа, вид діяльності, середня кількість працівників, адреса, одиниця виміру,
 * «Складено за…», назва форми з номером і кодом за ДКУД; таблиця «Стаття | Код рядка | графи»
 * з розділами, «Пасив» окремим заголовком у балансі, Ф4 — вісім граф; підписи.
 *
 * Редактор: рядки з формулою чи без — поля вводу (гривні з копійками, жовті), підсумкові —
 * сірі (сервер перераховує їх при збереженні). Друк: суми в тис. грн, як у бланку, відʼємні
 * і «у дужках» — у дужках. Стилі — спільні з бланком ПДВ (VatDeclarationBlank).
 */
import type { CSSProperties, ReactNode } from 'react';
import type { FinFormDef, FinReportLineDef, FinancialStatement } from '@/api/financialStatements';
import { BALANCE_DATE, EQUITY_COLUMNS, PERIOD_LABELS, fmtUah, toThousands } from '@/api/financialStatements';
import type { Organization } from '@/api/catalogs';
import { B, SERIF, XMark, blankBox, nameCell, numCell, thBlank, valCell } from '@/pages/taxation/VatDeclarationBlank';
import { chiefForReports } from '@/shared/utils/orgOfficials';

/** Назва, номер форми і код за ДКУД — як надруковано в бланку. */
export const FORM_SHEET: Record<string, { title: string; form: string; dkud: string; standard: string }> = {
  '1': { title: 'Баланс (Звіт про фінансовий стан)', form: '1', dkud: '1801001', standard: 'Додаток 1 до Національного положення (стандарту) бухгалтерського обліку 1 «Загальні вимоги до фінансової звітності»' },
  '2': { title: 'Звіт про фінансові результати (Звіт про сукупний дохід)', form: '2', dkud: '1801003', standard: 'Додаток 1 до Національного положення (стандарту) бухгалтерського обліку 1 «Загальні вимоги до фінансової звітності»' },
  '3': { title: 'Звіт про рух грошових коштів (за прямим методом)', form: '3', dkud: '1801004', standard: 'Додаток 1 до Національного положення (стандарту) бухгалтерського обліку 1 «Загальні вимоги до фінансової звітності»' },
  '4': { title: 'Звіт про власний капітал', form: '4', dkud: '1801005', standard: 'Додаток 1 до Національного положення (стандарту) бухгалтерського обліку 1 «Загальні вимоги до фінансової звітності»' },
  '1m': { title: '1. Баланс', form: '1-м', dkud: '1801006', standard: 'Додаток 1 до Національного положення (стандарту) бухгалтерського обліку 25 «Спрощена фінансова звітність» (пункт 4 розділу I)' },
  '2m': { title: '2. Звіт про фінансові результати', form: '2-м', dkud: '1801007', standard: 'Додаток 1 до Національного положення (стандарту) бухгалтерського обліку 25 «Спрощена фінансова звітність» (пункт 4 розділу I)' },
  '1ms': { title: '1. Баланс', form: '1-мс', dkud: '1801006', standard: 'Додаток 2 до Національного положення (стандарту) бухгалтерського обліку 25 «Спрощена фінансова звітність» (пункт 5 розділу I)' },
  '2ms': { title: '2. Звіт про фінансові результати', form: '2-мс', dkud: '1801007', standard: 'Додаток 2 до Національного положення (стандарту) бухгалтерського обліку 25 «Спрощена фінансова звітність» (пункт 5 розділу I)' },
};
/** Код електронної форми ДПС аркуша (для підпису «Форма …» у кутку). */
const XSD_CODE: Record<string, string> = {
  '1': 'J0900108', '2': 'J0900207', '3': 'J0900904', '4': 'J0901005', SMALL: 'J0901107', MICRO: 'J0901205',
};
const SET_TITLE: Record<string, string> = {
  SMALL: 'Фінансова звітність малого підприємства', MICRO: 'Фінансова звітність мікропідприємства',
};

const small: CSSProperties = { fontSize: '0.66rem', color: '#444', fontFamily: SERIF };
const label: CSSProperties = { fontFamily: SERIF, fontSize: '0.8rem', color: '#111' };
const ro = (v: string | number | null | undefined, align: CSSProperties['textAlign'] = 'left', print?: boolean): ReactNode => (
  <span style={{ display: 'inline-block', minWidth: 60, padding: '1px 4px', fontWeight: 600, textAlign: align, fontFamily: SERIF,
    fontSize: '0.82rem', background: print ? 'transparent' : '#eef1f4', color: '#111', borderBottom: print ? B : 'none', minHeight: '1.1em' }}>
    {v == null || v === '' ? ' ' : v}
  </span>
);
const codeBox = (v: string | number | null | undefined): ReactNode => (
  <span style={{ display: 'inline-block', minWidth: 90, border: B, padding: '1px 6px', textAlign: 'center', fontFamily: SERIF,
    fontSize: '0.82rem', fontWeight: 700, color: '#111', background: '#fff' }}>{v || ' '}</span>
);

export function columnLabels(form: FinFormDef, _year?: number, _quarter?: number): [string, string] {
  if (form.kind === 'balance') return ['На початок звітного періоду', 'На кінець звітного періоду'];
  return ['За звітний період', 'За аналогічний період попереднього року'];
}

function groupBySection(lines: FinReportLineDef[]): { section: string; lines: FinReportLineDef[] }[] {
  const out: { section: string; lines: FinReportLineDef[] }[] = [];
  for (const ln of lines) {
    const sec = ln.section ?? '';
    const last = out[out.length - 1];
    if (last && last.section === sec) last.lines.push(ln);
    else out.push({ section: sec, lines: [ln] });
  }
  return out;
}

function printCell(v: string | undefined, decimals: number, negative: boolean): string {
  const n = Number(v ?? 0);
  if (!n) return '—';
  const t = toThousands(Math.abs(n), decimals);
  return negative || n < 0 ? `(${t})` : t;
}

/** Шапка аркуша фінзвітності з блоком «КОДИ» — офіційний вигляд. */
export function FinSheetHeader({ doc, org, formCode, print }: {
  doc: FinancialStatement; org?: Organization; formCode: string; print?: boolean;
}) {
  const f = doc.fields ?? {};
  const sheet = FORM_SHEET[formCode];
  const d = doc.document_date.slice(0, 10).split('-');
  const dateCodes = `${d[0]} ${d[1]} ${d[2]}`;
  const full = doc.form_set === 'FULL';
  const unit = full
    ? 'тис. грн без десяткового знака (окрім розділу IV Звіту про фінансові результати (Звіту про сукупний дохід) (форма № 2), грошові показники якого наводяться в гривнях з копійками)'
    : 'тис. грн з одним десятковим знаком';
  const period = formCode.startsWith('1')
    ? `на ${BALANCE_DATE[doc.period_quarter]} ${doc.period_year} р.`
    : `за ${PERIOD_LABELS[doc.period_quarter] === 'рік' ? 'рік' : PERIOD_LABELS[doc.period_quarter]} ${doc.period_year} р.`;
  const row = (left: ReactNode, codeLabel?: string, code?: string | number | null) => (
    <tr>
      <td style={{ ...label, padding: '2px 4px' }}>{left}</td>
      <td style={{ ...label, padding: '2px 4px', textAlign: 'right', whiteSpace: 'nowrap', width: 110 }}>{codeLabel ?? ''}</td>
      <td style={{ padding: '2px 4px', width: 110 }}>{codeLabel ? codeBox(code) : null}</td>
    </tr>
  );
  return (
    <div style={{ ...blankBox, padding: '4px 6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', ...small }}>
        <span>Форма {XSD_CODE[doc.form_set === 'FULL' ? formCode : doc.form_set] ?? ''}</span>
        <span style={{ textAlign: 'right', maxWidth: 360 }}>{sheet?.standard}</span>
      </div>
      {!full && <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.95rem', fontFamily: SERIF, margin: '2px 0' }}>{SET_TITLE[doc.form_set]}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr><td /><td /><td style={{ textAlign: 'center', ...small, fontWeight: 700 }}>КОДИ</td></tr>
          {row(<span />, 'Дата (рік, місяць, число)', dateCodes)}
          {row(<>Підприємство {ro(f.name || org?.full_name || org?.name, 'left', print)}</>, 'за ЄДРПОУ', f.tin || org?.edrpou)}
          {row(<>Територія {ro(f.koatuu_name, 'left', print)}</>, 'за КАТОТТГ', f.katottg || (/^UA\d{17}$/.test(org?.koatuu_code ?? '') ? org?.koatuu_code : ''))}
          {row(<>Організаційно-правова форма господарювання {ro(f.kopfg_name || org?.opfg_name, 'left', print)}</>, 'за КОПФГ', f.kopfg || org?.opfg_code)}
          {row(<>Вид економічної діяльності {ro(f.kved_name, 'left', print)}</>, 'за КВЕД', f.kved || org?.kved_main)}
          {row(<>Середня кількість працівників{full ? <sup>1</sup> : ', осіб'} {ro(f.kil ?? '', 'center', print)}</>)}
          {row(<>Адреса, телефон {ro(f.loc || org?.legal_address, 'left', print)} {ro(f.tel || org?.phone, 'left', print)}</>)}
          {row(<>Одиниця виміру: {unit}</>)}
          {full && row(
            <>Складено (зробити позначку «v» у відповідній клітинці):
              <div style={{ display: 'flex', gap: 16, marginTop: 2 }}>
                <span>за положеннями (стандартами) бухгалтерського обліку <XMark on={(f.standards ?? 'NPSBO') === 'NPSBO'} /></span>
                <span>за міжнародними стандартами фінансової звітності <XMark on={f.standards === 'IFRS'} /></span>
              </div>
            </>,
          )}
        </tbody>
      </table>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.95rem', fontFamily: SERIF, marginTop: 6 }}>
        {sheet?.title}
        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{period}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, ...label, marginTop: 2 }}>
        <span>Форма № {sheet?.form}</span><span>Код за ДКУД {codeBox(sheet?.dkud)}</span>
      </div>
    </div>
  );
}

function sumCell(v: string | undefined, editable: boolean, onChange?: (raw: string) => void, print?: boolean, decimals = 0,
                 negative = false, thousands?: boolean, ariaLabel?: string) {
  if (print) return <td style={{ ...valCell, textAlign: 'right', fontFamily: SERIF, fontSize: '0.8rem' }}>{printCell(v, decimals, negative)}</td>;
  if (!editable || !onChange) {
    return (
      <td style={valCell}>
        <span style={{ display: 'block', padding: '2px 4px', textAlign: 'right', fontFamily: SERIF, fontSize: '0.82rem', fontWeight: 600, background: '#eef1f4', color: '#111', minHeight: '1.1em' }}>
          {thousands ? toThousands(v, decimals) : fmtUah(v)}
        </span>
      </td>
    );
  }
  return (
    <td style={valCell}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {thousands && <small style={{ color: '#555', fontFamily: SERIF, whiteSpace: 'nowrap' }}>{toThousands(v, decimals)}</small>}
        <input type="number" step="0.01" value={v ?? ''} aria-label={ariaLabel} onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', fontFamily: SERIF, fontSize: '0.82rem',
            textAlign: 'right', padding: '2px 4px', background: '#fefce8', color: '#111' }} />
      </div>
    </td>
  );
}

/** Форми з двома графами (Ф1, Ф2, Ф3, 1-м, 2-м, 1-мс, 2-мс) — таблиця бланка. */
export function FinStatementForm({ form, doc, values, setV, readOnly, print, thousands }: {
  form: FinFormDef;
  doc: FinancialStatement;
  values: Record<string, string>;
  setV?: (code: string, raw: string) => void;
  readOnly?: boolean;
  print?: boolean;
  /** показувати поруч із полем суму в тис. грн, як вона піде у файл */
  thousands?: boolean;
}) {
  const [c3, c4] = columnLabels(form, doc.period_year, doc.period_quarter);
  const editable = !print && !readOnly && !!setV;
  const balance = form.kind === 'balance';
  const sections = groupBySection(form.lines);
  // Пасив балансу починається з першого розділу про власний капітал.
  const passiveIdx = balance ? sections.findIndex((s) => /власний капітал/i.test(s.section)) : -1;
  const head = (first: string) => (
    <>
      <tr>
        <th style={{ ...thBlank, textAlign: 'center' }}>{first}</th>
        <th style={thBlank}>Код рядка</th>
        <th style={thBlank}>{c3}</th>
        <th style={thBlank}>{c4}</th>
      </tr>
      <tr><th style={thBlank}>1</th><th style={thBlank}>2</th><th style={thBlank}>3</th><th style={thBlank}>4</th></tr>
    </>
  );
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
      <colgroup><col /><col style={{ width: 64 }} /><col style={{ width: 150 }} /><col style={{ width: 150 }} /></colgroup>
      <thead>{head(balance ? 'Актив' : 'Стаття')}</thead>
      {sections.map((sect, si) => (
        <tbody key={`${sect.section}-${si}`}>
          {si === passiveIdx && head('Пасив')}
          {sect.section && (
            <tr><td colSpan={4} style={{ ...nameCell, fontWeight: 700, textAlign: 'center' }}>{sect.section}</td></tr>
          )}
          {sect.lines.map((ln) => {
            const canEdit = editable && !ln.is_total;
            return (
              <tr key={ln.line_code} style={{ fontWeight: ln.is_total ? 700 : 400 }}>
                <td style={{ ...nameCell, paddingLeft: ln.parent_line ? '1.6rem' : undefined }}>
                  {ln.line_name}{ln.is_manual && !print ? <span style={small}> (вручну)</span> : null}
                </td>
                <td style={numCell}>{ln.line_code}</td>
                {sumCell(values[`R${ln.line_code}G3`], canEdit, setV ? (raw) => setV(`R${ln.line_code}G3`, raw) : undefined, print, form.decimals, ln.is_negative, thousands, `${ln.line_code} ${c3}`)}
                {sumCell(values[`R${ln.line_code}G4`], canEdit, setV ? (raw) => setV(`R${ln.line_code}G4`, raw) : undefined, print, form.decimals, ln.is_negative, thousands, `${ln.line_code} ${c4}`)}
              </tr>
            );
          })}
        </tbody>
      ))}
    </table>
  );
}

/** Ф4 «Звіт про власний капітал»: матриця граф 3–10 як у бланку. */
export function EquityMatrix({ form, values, setV, readOnly, print, thousands }: {
  form: FinFormDef;
  values: Record<string, string>;
  setV?: (code: string, raw: string) => void;
  readOnly?: boolean;
  print?: boolean;
  thousands?: boolean;
}) {
  const editable = !print && !readOnly && !!setV;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox }}>
        <thead>
          <tr>
            <th style={{ ...thBlank, textAlign: 'center' }}>Стаття</th>
            <th style={thBlank}>Код рядка</th>
            {EQUITY_COLUMNS.map((c) => <th key={c.col} style={{ ...thBlank, minWidth: print ? 64 : 104 }}>{c.title}</th>)}
          </tr>
          <tr>{['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((n) => <th key={n} style={thBlank}>{n}</th>)}</tr>
        </thead>
        <tbody>
          {form.lines.map((ln) => (
            <tr key={ln.line_code} style={{ fontWeight: ln.is_total ? 700 : 400 }}>
              <td style={{ ...nameCell, paddingLeft: ln.parent_line ? '1.6rem' : undefined }}>{ln.line_name}</td>
              <td style={numCell}>{ln.line_code}</td>
              {EQUITY_COLUMNS.map((c) => {
                const key = `R${ln.line_code}${c.col}`;
                const canEdit = editable && !ln.is_total && c.col !== 'G10';
                return (
                  <td key={c.col} style={{ ...valCell, width: undefined }}>
                    {print
                      ? <span style={{ display: 'block', textAlign: 'right', fontFamily: SERIF, fontSize: '0.76rem' }}>{printCell(values[key], form.decimals, false)}</span>
                      : canEdit
                        ? <input type="number" step="0.01" value={values[key] ?? ''} aria-label={`${ln.line_code} ${c.col}`} onChange={(e) => setV?.(key, e.target.value)}
                            style={{ width: 96, boxSizing: 'border-box', border: 'none', outline: 'none', fontFamily: SERIF, fontSize: '0.8rem', textAlign: 'right', padding: '2px 4px', background: '#fefce8', color: '#111' }} />
                        : <span style={{ display: 'block', padding: '2px 4px', textAlign: 'right', fontFamily: SERIF, fontSize: '0.8rem', fontWeight: 600, background: '#eef1f4', color: '#111', minHeight: '1.1em' }}>
                            {thousands ? toThousands(values[key], form.decimals) : fmtUah(values[key])}
                          </span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Підписи під формою: керівник і головний бухгалтер. */
export function FinSignatures({ org, print }: { org?: Organization; print?: boolean }) {
  const chief = org ? chiefForReports(org) : null;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox, marginTop: 6 }}>
      <tbody>
        <tr>
          <td style={{ ...nameCell, width: 180 }}>Керівник</td>
          <td style={{ ...valCell, width: 160 }}><span style={small}>(підпис)</span></td>
          <td style={valCell}>{ro(org?.director_name, 'left', print)}</td>
        </tr>
        <tr>
          <td style={nameCell}>Головний бухгалтер</td>
          <td style={valCell}><span style={small}>(підпис)</span></td>
          <td style={valCell}>{ro(chief ? org?.chief_accountant : org?.director_name, 'left', print)}</td>
        </tr>
      </tbody>
    </table>
  );
}

/** Старий друк шапки — лишається для сумісності (друк використовує FinSheetHeader). */
export function FinStatementHeaderPrint({ doc, org }: { doc: FinancialStatement; org?: Organization }) {
  return <FinSheetHeader doc={doc} org={org} formCode={doc.form_set === 'FULL' ? '1' : doc.form_set === 'SMALL' ? '1m' : '1ms'} print />;
}
